import classNames from 'classnames/bind'
import { ComponentProps, useRef } from 'react'
import { Autoplay, Pagination } from 'swiper'
import { Swiper, SwiperSlide } from 'swiper/react'
import type { Swiper as SwiperType } from 'swiper/types'

// Libs
import useMedia from '@/hooks/useMedia'

// Components
import LiveItem from '@/components/shared/LiveItem'
import Navigation from '@/components/shared/Navigation'

// Styles
import styles from './style.module.scss'
const cx = classNames.bind(styles)

interface ILiveSlideProps {
  autoplay?: boolean
  liveRooms: ComponentProps<typeof LiveItem>[]
  fetchNextPage: () => void
  maxNumber?: number
}

// 每次滑到 5 倍數的頁數，會觸發 fetchNextPage
const FETCH_NEXT_PAGE_THRESHOLD = 5

const LiveSlide = ({ autoplay = false, liveRooms, fetchNextPage, maxNumber }: ILiveSlideProps) => {
  const currentPageRef = useRef(0)
  const swiperRef = useRef<SwiperType>()

  const { isMobile } = useMedia()

  const onSlideChange = (swiper: SwiperType) => {
    const { realIndex, isEnd } = swiper
    const isIncreasing = realIndex > currentPageRef.current
    const needFetchNextPage = (realIndex + 1) % FETCH_NEXT_PAGE_THRESHOLD === 0
    const isMaxNumber = maxNumber && liveRooms.length >= maxNumber
    if (((isIncreasing && needFetchNextPage) || isEnd) && !isMaxNumber) {
      fetchNextPage()
    }
    currentPageRef.current = realIndex
  }

  const shouldHideButtons = () => {
    if (!swiperRef.current) return true

    const slidesPerView = swiperRef.current.params.slidesPerView
    const totalSlides = swiperRef.current.slides.length

    return totalSlides > (typeof slidesPerView === 'string' ? parseInt(slidesPerView) : slidesPerView || 0)
  }

  const canSlide = shouldHideButtons()

  return (
    <div className={cx('live-slide')}>
      {!isMobile && canSlide && (
        <Navigation className={cx('live-slide-arrow', 'left')} direction='left' onClick={() => swiperRef.current?.slidePrev()} />
      )}

      <Swiper
        className={cx('live-slide__carousel')}
        breakpoints={{
          360: {
            slidesPerView: 2,
          },
          710: {
            slidesPerView: 3,
          },
          1024: {
            slidesPerView: 5,
          },
        }}
        slidesPerGroup={1}
        spaceBetween={10}
        pagination={{
          clickable: true,
          el: '.pagination-container',
        }}
        modules={[Pagination, Autoplay]}
        speed={500}
        autoplay={
          autoplay
            ? {
                delay: 5000,
                disableOnInteraction: false,
              }
            : false
        }
        onSlideChange={onSlideChange}
        onSwiper={(swiper: any) => (swiperRef.current = swiper)}
      >
        {liveRooms.map((item, rowIndex) => (
          <SwiperSlide
            key={rowIndex}
            style={{
              display: 'flex',
              gap: '20px',
            }}
          >
            <LiveItem {...item} />
          </SwiperSlide>
        ))}
        <div
          className='swiper-pagination pagination-container'
          style={{ position: 'static', gap: '10px', justifyContent: 'center', marginTop: '20px' }}
        ></div>
      </Swiper>
      {!isMobile && canSlide && (
        <Navigation className={cx('live-slide-arrow', 'right')} direction='right' onClick={() => swiperRef.current?.slideNext()} />
      )}
    </div>
  )
}

export default LiveSlide
